import { useEffect, useId, useMemo, useRef, useState, type MouseEvent } from 'react';

import { MARKETS } from '../config.ts';
import { WAD } from '../lib/format.ts';
import { blockAgeSec, closedNet } from '../lib/positions.ts';
import type { Globals } from '../lib/protocol.ts';
import type { ApiClosed } from '../lib/relayer.ts';

const RANGES = [
  { id: '24h', label: '24H', sec: 86_400 },
  { id: '7d', label: '7D', sec: 7 * 86_400 },
  { id: 'all', label: 'All', sec: Infinity },
] as const;
type RangeId = (typeof RANGES)[number]['id'];

const AXIS_W = 64;
const AXIS_H = 20;
const PAD_TOP = 10;
const H = 210;

type Point = { cum: number; pnl: number; at?: number; h?: ApiClosed };

const usd = (v: number, digits = 2) =>
  `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const signed = (v: number) => `${v >= 0 ? '+' : ''}${usd(v)}`;

/** 1, 2 or 5 times a power of ten: axis steps a reader can add up */
function niceStep(span: number, ticks: number): number {
  const raw = span / ticks;
  const p = 10 ** Math.floor(Math.log10(raw));
  const m = raw / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

function axisLabel(v: number): string {
  const a = Math.abs(v);
  const s = a >= 1e6 ? `${(a / 1e6).toFixed(1)}M` : a >= 1e3 ? `${(a / 1e3).toFixed(a >= 1e4 ? 0 : 1)}K` : a.toFixed(a >= 10 || a === 0 ? 0 : 1);
  return `${v < 0 ? '-' : ''}$${s}`;
}

function timeLabel(sec: number, spanSec: number): string {
  const d = new Date(sec * 1000);
  return spanSec < 86_400
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Cumulative realized PnL, one point per closed trade, net of fees. The line is green above zero and red below,
 * hovering shows the trade behind each step.
 */
export function PnlChart({ history, globals, block }: { history: ApiClosed[]; globals?: Globals; block?: bigint }) {
  const [range, setRange] = useState<RangeId>('all');
  const [hover, setHover] = useState<number>();
  const [w, setW] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, '');

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // oldest first; the window starts at zero so each range reads as "PnL over that period"
  const pts = useMemo<Point[]>(() => {
    const span = RANGES.find((r) => r.id === range)!.sec;
    const now = Date.now() / 1000;
    const rows = [...history]
      .reverse()
      .map((h) => ({ h, at: blockAgeSec(block, h.blockNumber), pnl: Number((closedNet(h, globals) * 100n) / WAD) / 100 }))
      .filter((r) => span === Infinity || (r.at !== undefined && now - r.at <= span));
    const out: Point[] = [{ cum: 0, pnl: 0, at: rows[0]?.at }];
    for (const r of rows) out.push({ cum: out[out.length - 1]!.cum + r.pnl, pnl: r.pnl, at: r.at, h: r.h });
    return out;
  }, [history, globals, block, range]);

  const trades = pts.slice(1);
  const total = pts[pts.length - 1]!.cum;
  const wins = trades.filter((p) => p.pnl > 0).length;
  const best = trades.reduce((a, p) => Math.max(a, p.pnl), -Infinity);
  const worst = trades.reduce((a, p) => Math.min(a, p.pnl), Infinity);
  let peak = 0;
  let drawdown = 0;
  for (const p of pts) {
    peak = Math.max(peak, p.cum);
    drawdown = Math.max(drawdown, peak - p.cum);
  }

  const pw = Math.max(0, w - AXIS_W);
  const ph = H - AXIS_H - PAD_TOP;
  const rawLo = Math.min(0, ...pts.map((p) => p.cum));
  const rawHi = Math.max(0, ...pts.map((p) => p.cum));
  const step = niceStep(rawHi - rawLo || 1, 4);
  const lo = Math.floor(rawLo / step) * step;
  const ceil = Math.ceil(rawHi / step) * step;
  const hi = ceil > lo ? ceil : lo + step;
  const x = (i: number) => (pts.length === 1 ? pw / 2 : (i / (pts.length - 1)) * pw);
  const y = (v: number) => PAD_TOP + ((hi - v) / (hi - lo)) * ph;
  const y0 = y(0);
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(v);

  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.cum).toFixed(1)}`).join('');
  const area = `${line}L${x(pts.length - 1).toFixed(1)},${y0}L0,${y0}Z`;
  const first = pts[0]!.at;
  const lastAt = pts[pts.length - 1]!.at;
  const spanSec = first !== undefined && lastAt !== undefined ? lastAt - first : 0;
  const hp = hover !== undefined ? pts[hover] : undefined;

  function onMove(e: MouseEvent<SVGSVGElement>) {
    if (pts.length < 2) return;
    const bx = e.clientX - e.currentTarget.getBoundingClientRect().left;
    const i = Math.round((Math.min(Math.max(bx, 0), pw) / pw) * (pts.length - 1));
    setHover(i === 0 ? 1 : i);
  }

  return (
    <div className="pnl-chart">
      <div className="pnl-head">
        <div>
          <div className={`pnl-total num ${total >= 0 ? 'pos' : 'neg'}`}>{signed(total)}</div>
          <div className="pnl-sub">
            {trades.length === 0 ? 'No closed trades in this period' : `${trades.length} trade${trades.length > 1 ? 's' : ''} · net of fees`}
          </div>
        </div>
        <div className="seg seg-sm" role="tablist">
          {RANGES.map((r) => (
            <button key={r.id} className={r.id === range ? 'is-on' : ''} onClick={() => setRange(r.id)}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pnl-plot" ref={wrap}>
        {w > 0 && (
          <svg width={w} height={H} onMouseMove={onMove} onMouseLeave={() => setHover(undefined)} role="img" aria-label="Cumulative realized PnL">
            <defs>
              <linearGradient id={`up${id}`} x1="0" x2="0" y1={PAD_TOP} y2={y0} gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="var(--pos)" stopOpacity="0.32" />
                <stop offset="1" stopColor="var(--pos)" stopOpacity="0.02" />
              </linearGradient>
              <linearGradient id={`dn${id}`} x1="0" x2="0" y1={y0} y2={PAD_TOP + ph} gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="var(--neg)" stopOpacity="0.02" />
                <stop offset="1" stopColor="var(--neg)" stopOpacity="0.32" />
              </linearGradient>
              <clipPath id={`above${id}`}>
                <rect x="0" y="0" width={pw} height={y0} />
              </clipPath>
              <clipPath id={`below${id}`}>
                <rect x="0" y={y0} width={pw} height={H - y0} />
              </clipPath>
            </defs>

            {ticks.map((v) => (
              <g key={v}>
                <line x1="0" x2={pw} y1={y(v)} y2={y(v)} className={v === 0 ? 'zero' : 'grid'} />
                <text x={pw + 8} y={y(v)} dy="0.32em" className="axis">
                  {axisLabel(v)}
                </text>
              </g>
            ))}

            <path d={area} fill={`url(#up${id})`} clipPath={`url(#above${id})`} />
            <path d={area} fill={`url(#dn${id})`} clipPath={`url(#below${id})`} />
            <path d={line} className="line line-pos" clipPath={`url(#above${id})`} />
            <path d={line} className="line line-neg" clipPath={`url(#below${id})`} />

            {/* a dot per trade while they stay readable */}
            {trades.length <= 60 &&
              trades.map((p, k) => <circle key={k} cx={x(k + 1)} cy={y(p.cum)} r={2.5} className={p.pnl >= 0 ? 'dot-pos' : 'dot-neg'} />)}

            {first !== undefined && lastAt !== undefined && trades.length > 0 && (
              <>
                <text x={0} y={H - 5} className="axis">
                  {timeLabel(first, spanSec)}
                </text>
                <text x={pw} y={H - 5} textAnchor="end" className="axis">
                  {timeLabel(lastAt, spanSec)}
                </text>
              </>
            )}

            {hp && hover !== undefined && (
              <g className="cross">
                <line x1={x(hover)} x2={x(hover)} y1={PAD_TOP} y2={PAD_TOP + ph} />
                <circle cx={x(hover)} cy={y(hp.cum)} r={4.5} className={hp.cum >= 0 ? 'dot-pos' : 'dot-neg'} />
              </g>
            )}
          </svg>
        )}
        {trades.length === 0 && <div className="pnl-empty">No closed trades yet</div>}
        {hp?.h && hover !== undefined && (
          <div className="pnl-tip" style={x(hover) > pw / 2 ? { right: w - x(hover) + 12 } : { left: x(hover) + 12 }}>
            <div className="pnl-tip-head">
              <b>{`${MARKETS[hp.h.instrumentId]?.symbol ?? '?'} ${hp.h.isLong ? 'Long' : 'Short'} ${hp.h.leverage}×`}</b>
              {hp.h.outcome === 'liquidated' && <span className="tag tag-liq">Liq</span>}
            </div>
            <div className="row">
              <span>Trade</span>
              <b className={`num ${hp.pnl >= 0 ? 'pos' : 'neg'}`}>{signed(hp.pnl)}</b>
            </div>
            <div className="row">
              <span>Cumulative</span>
              <b className={`num ${hp.cum >= 0 ? 'pos' : 'neg'}`}>{signed(hp.cum)}</b>
            </div>
            {hp.at !== undefined && <div className="pnl-tip-time">{new Date(hp.at * 1000).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
          </div>
        )}
      </div>

      <div className="pnl-stats">
        <div>
          <span>Win rate</span>
          <b className="num">{trades.length ? `${Math.round((wins / trades.length) * 100)}%` : '- -'}</b>
          <small>{trades.length ? `${wins}W / ${trades.length - wins}L` : ''}</small>
        </div>
        <div>
          <span>Best trade</span>
          <b className={`num ${best > 0 ? 'pos' : ''}`}>{trades.length ? signed(best) : '- -'}</b>
        </div>
        <div>
          <span>Worst trade</span>
          <b className={`num ${worst < 0 ? 'neg' : ''}`}>{trades.length ? signed(worst) : '- -'}</b>
        </div>
        <div>
          <span>Max drawdown</span>
          <b className={`num ${drawdown > 0 ? 'neg' : ''}`}>{trades.length ? usd(-drawdown) : '- -'}</b>
        </div>
      </div>
    </div>
  );
}
